import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Fade,
  Chip,
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Build as BuildIcon,
  Group as GroupIcon,
  Euro as EuroIcon,
  Assignment as AssignmentIcon,
  Security as SecurityIcon,
  Today as TodayIcon,
} from '@mui/icons-material';
import * as timeEntriesAPI from '../../services/timeEntriesAPI';
import * as projectsAPI from '../../services/projectsAPI';
import { useSnackbar } from 'notistack';
import { TimeClockMultiDevice } from '../../components/TimeClock';
import { ActiveTimers } from '../../components/ActiveTimers';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

interface SimpleChartProps {
  title: string;
  data: Array<{ name: string; value: number; unit?: string }>;
  color: string;
}

const SimpleChart: React.FC<SimpleChartProps> = ({ title, data, color }) => {
  if (data.length === 0) {
    return (
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" py={4}>
            Keine Daten verfügbar
          </Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        <Box sx={{ mt: 2 }}>
          {data.slice(0, 5).map((item, index) => (
            <Box key={index} sx={{ py: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" sx={{ flex: 1 }}>
                {item.name}
              </Typography>
              <Typography variant="body2" fontWeight={600} color="primary">
                {item.value}{item.unit || ''}
              </Typography>
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
};

export default function DashboardOverview() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalHours: 0,
    activeProjects: 0,
    monthlyRevenue: 0,
    totalUsers: 5,
  });
  const [recentProjects, setRecentProjects] = useState<any[]>([]);
  const [recentEntries, setRecentEntries] = useState<any[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  // Rolle-basierte Berechtigungen
  const isAdmin = user?.role === 'admin';
  const isManagement = user?.role === 'management';
  const isBauleiter = user?.role === 'bauleiter';
  const isForeman = user?.role === 'foreman';
  const isInstaller = user?.role === 'installer';
  
  // Admin, Management & Bauleiter sehen alles
  const canSeeAll = isAdmin || isManagement || isBauleiter;
  // Vorarbeiter & Mitarbeiter sehen nur bestimmte Elemente
  const canSeeRestricted = isForeman || isInstaller;
  
  const canCreateProjects = isAdmin;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [timeEntries, projects] = await Promise.all([
          timeEntriesAPI.getEntries(),
          projectsAPI.getProjects()
        ]);

        // Calculate stats
        const totalHours = timeEntries.reduce((sum: number, entry: any) => {
          const start = new Date(entry.start_time);
          const end = new Date(entry.end_time);
          const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60) - (entry.break_duration || 0) / 60;
          return sum + hours;
        }, 0);
        
        const activeProjects = projects.filter((p: any) => p.status === 'active').length;
        const monthlyRevenue = projects
          .filter((p: any) => p.status === 'active')
          .reduce((sum: number, p: any) => sum + (p.contract_amount || 0), 0);

        setStats({
          totalHours: Math.round(totalHours * 10) / 10,
          activeProjects,
          monthlyRevenue,
          totalUsers: 5,
        });

        // Top projects by revenue - nur Admin & Bauleiter
        if (canSeeAll) {
          const projectData = projects
            .filter((p: any) => p.contract_amount > 0)
            .sort((a: any, b: any) => (b.contract_amount || 0) - (a.contract_amount || 0))
            .slice(0, 5)
            .map((p: any) => ({
              name: p.name,
              value: p.contract_amount || 0,
              unit: ' €'
            }));
          setRecentProjects(projectData);
        } else {
          setRecentProjects([]);
        }

        // Recent time entries
        const entryData = timeEntries
          .slice(0, 5)
          .map((entry: any) => {
            const start = new Date(entry.start_time);
            const end = new Date(entry.end_time);
            const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60) - (entry.break_duration || 0) / 60;
            return {
              name: entry.description || 'Unbekannt',
              value: Math.round(hours * 10) / 10,
              unit: ' h'
            };
          });

        setRecentEntries(entryData);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        enqueueSnackbar('Fehler beim Laden der Dashboard-Daten', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [enqueueSnackbar, canSeeAll]);

  const handleClockStop = (start: Date, end: Date, pause: number, startLat?: number, startLng?: number, endLat?: number, endLng?: number) => {
    navigate('/time-tracking/new', {
      state: {
        start_time: start.toISOString(),
        end_time: end.toISOString(),
        break_duration: Math.round(pause / 60),
        date: start.toISOString().split('T')[0],
        start_latitude: startLat,
        start_longitude: startLng,
        end_latitude: endLat,
        end_longitude: endLng,
      },
    });
  };

  // Dashboard-Karten basierend auf Rolle - nur für Admin & Bauleiter
  const statCards = canSeeAll ? [
    {
      title: 'Gesamtstunden',
      value: `${stats.totalHours}h`,
      change: 12,
      icon: <AccessTimeIcon />,
      color: '#667eea',
    },
    {
      title: 'Aktive Projekte',
      value: stats.activeProjects,
      change: 5,
      icon: <BuildIcon />,
      color: '#10b981',
    },
    {
      title: 'Monatsumsatz',
      value: `€${stats.monthlyRevenue.toLocaleString()}`,
      change: 8,
      icon: <EuroIcon />,
      color: '#f59e0b',
    },
    {
      title: 'Team Mitglieder',
      value: stats.totalUsers,
      change: 0,
      icon: <GroupIcon />,
      color: '#8b5cf6',
    },
  ] : [];

  return (
    <Fade in timeout={800}>
      <Box>
        <Typography variant="h4" fontWeight={700} gutterBottom sx={{ color: 'text.primary', mb: 4 }}>
          Dashboard Übersicht
        </Typography>
        <Box mb={3}>
          <TimeClockMultiDevice onStop={handleClockStop} />
        </Box>
        
        {/* Active Timer Sessions - nur für Admin & Bauleiter */}
        {canSeeAll && (
          <Box mb={4}>
            <ActiveTimers refreshInterval={15000} />
          </Box>
        )}
        {/* Schnellzugriff - nur für Admin & Bauleiter */}
        {canSeeAll && (
          <Box mb={4}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Schnellzugriff
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2 }}>
              {/* Zeit erfassen */}
              <Chip
                icon={<AccessTimeIcon />}
                label="Zeit erfassen"
                variant="outlined"
                clickable
                onClick={() => navigate('/time-tracking/new')}
                sx={{ borderColor: '#667eea', color: '#667eea', '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.04)' } }}
              />
              {/* Neuer Regiebericht */}
              <Chip
                icon={<AssignmentIcon />}
                label="Neuer Regiebericht"
                variant="outlined"
                clickable
                onClick={() => navigate('/regie-reports/new')}
                sx={{ borderColor: '#10b981', color: '#10b981', '&:hover': { bgcolor: 'rgba(16, 185, 129, 0.04)' } }}
              />
              {/* Neuer Tagesbericht */}
              <Chip
                icon={<TodayIcon />}
                label="Neuer Tagesbericht"
                variant="outlined"
                clickable
                onClick={() => navigate('/tagesberichte/new')}
                sx={{ borderColor: '#06b6d4', color: '#06b6d4', '&:hover': { bgcolor: 'rgba(6, 182, 212, 0.04)' } }}
              />
              {/* Projekt erstellen - nur Admin */}
              {canCreateProjects && (
                <Chip
                  icon={<BuildIcon />}
                  label="Projekt erstellen"
                  variant="outlined"
                  clickable
                  onClick={() => navigate('/projects/new')}
                  sx={{ borderColor: '#f59e0b', color: '#f59e0b', '&:hover': { bgcolor: 'rgba(245, 158, 11, 0.04)' } }}
                />
              )}
              {/* Sekurant hinzufügen */}
              <Chip
                icon={<SecurityIcon />}
                label="Sekurant hinzufügen"
                variant="outlined"
                clickable
                onClick={() => navigate('/sekuranten/new-projekt')}
                sx={{ borderColor: '#8b5cf6', color: '#8b5cf6', '&:hover': { bgcolor: 'rgba(139, 92, 246, 0.04)' } }}
              />
            </Box>
          </Box>
        )}
        {/* Statistics Cards */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 3, mb: 4 }}>
          {statCards.map((card, index) => (
            <Card key={index} elevation={3}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2}>
                  {card.icon}
                  <Box>
                    <Typography variant="h6">{card.title}</Typography>
                    <Typography variant="h4" color="primary">
                      {card.value}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>

        {/* Charts and Recent Data - nur für Admin & Bauleiter */}
        {canSeeAll && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' }, gap: 3 }}>
            {/* Top Projekte (Umsatz) */}
            <Box>
              <SimpleChart
                title="Top Projekte (Umsatz)"
                data={recentProjects}
                color="#667eea"
              />
            </Box>
            {/* Letzte Zeiteinträge */}
            <Box>
              <SimpleChart
                title="Letzte Zeiteinträge"
                data={recentEntries}
                color="#10b981"
              />
            </Box>
          </Box>
        )}
      </Box>
    </Fade>
  );
} 